import { ExpeditedProgramSection } from "@/types/expeditedProgram.types";
import {
  EXPEDITED_PROGRAM,
  EXPEDITED_PROGRAM_CONTENTS,
  EXPEDITED_PROGRAM_GROUP_KEY as eg,
} from "@FillInTableContainer/shared/constants/expeditedProgramKey.constants";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  handleClickToClearExpeditedProgramContentsGroupShell: () => void;
  handleKeyDownToClearExpeditedProgramContentsGroupShell: (
    event: KeyboardEvent,
  ) => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: ExpeditedProgramSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useExpeditedProgramContentsGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const articleSection = EXPEDITED_PROGRAM;
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();

  const handleClearGroupShell = () => {
    const clearExpeditedProgramContentsGroupShell = (): ArticleEntryData => {
      if (info.groupShellKey === "first")
        return {
          ...entryData,
          [articleSection]: {
            ...entryData[articleSection],
            [eg.expeditedProgramContents]: entryData[articleSection]?.[
              eg.expeditedProgramContents
            ]?.map((tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.tableIndex) {
                return {
                  ...tableInfo,
                  [EXPEDITED_PROGRAM_CONTENTS.updateDate]: {
                    ...tableInfo[EXPEDITED_PROGRAM_CONTENTS.updateDate],
                    value: [],
                  },
                  [EXPEDITED_PROGRAM_CONTENTS.designationStatus]: {
                    ...tableInfo[EXPEDITED_PROGRAM_CONTENTS.designationStatus],
                    value: [],
                  },
                  [EXPEDITED_PROGRAM_CONTENTS.designatedCompanyName]: {
                    ...tableInfo[
                      EXPEDITED_PROGRAM_CONTENTS.designatedCompanyName
                    ],
                    value: [],
                  },
                  [EXPEDITED_PROGRAM_CONTENTS.designatedProduct]: {
                    ...tableInfo[EXPEDITED_PROGRAM_CONTENTS.designatedProduct],
                    value: [],
                  },
                  [EXPEDITED_PROGRAM_CONTENTS.regulatoryAgency]: {
                    ...tableInfo[EXPEDITED_PROGRAM_CONTENTS.regulatoryAgency],
                    value: [],
                  },
                };
              }
              return tableInfo;
            }),
          },
        };

      if (info.groupShellKey === "second")
        return {
          ...entryData,
          [articleSection]: {
            ...entryData[articleSection],
            [eg.expeditedProgramContents]: entryData[articleSection]?.[
              eg.expeditedProgramContents
            ]?.map((tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.tableIndex) {
                return {
                  ...tableInfo,
                  [EXPEDITED_PROGRAM_CONTENTS.expeditedProgram]: {
                    ...tableInfo[EXPEDITED_PROGRAM_CONTENTS.expeditedProgram],
                    value: [],
                  },
                  [EXPEDITED_PROGRAM_CONTENTS.designatedIndication]: {
                    ...tableInfo[
                      EXPEDITED_PROGRAM_CONTENTS.designatedIndication
                    ],
                    value: [],
                  },
                };
              }
              return tableInfo;
            }),
          },
        };

      if (info.groupShellKey === "third")
        return {
          ...entryData,
          [articleSection]: {
            ...entryData[articleSection],
            [eg.expeditedProgramContents]: entryData[articleSection]?.[
              eg.expeditedProgramContents
            ]?.map((tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.tableIndex) {
                return {
                  ...tableInfo,
                  [EXPEDITED_PROGRAM_CONTENTS.designationBasisClinicalSignificance]:
                    {
                      ...tableInfo[
                        EXPEDITED_PROGRAM_CONTENTS
                          .designationBasisClinicalSignificance
                      ],
                      value: [],
                    },
                  [EXPEDITED_PROGRAM_CONTENTS.programBenefitsIncentives]: {
                    ...tableInfo[
                      EXPEDITED_PROGRAM_CONTENTS.programBenefitsIncentives
                    ],
                    value: [],
                  },
                };
              }
              return tableInfo;
            }),
          },
        };

      return {
        ...entryData,
      };
    };

    if (info.groupTableKey === eg.expeditedProgramContents) {
      setEntryData(clearExpeditedProgramContentsGroupShell());
      changeCurrentHistory({
        entryData: clearExpeditedProgramContentsGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }
  };

  const handleClickToClearExpeditedProgramContentsGroupShell = () => {
    if (focusStatus.type === "clear") {
      handleClearGroupShell();
    }
  };

  const handleKeyDownToClearExpeditedProgramContentsGroupShell = (
    event: KeyboardEvent,
  ) => {
    if (focusStatus.type === "clear" && event.key === "Delete") {
      handleClearGroupShell();
    }
  };

  return {
    handleClickToClearExpeditedProgramContentsGroupShell,
    handleKeyDownToClearExpeditedProgramContentsGroupShell,
  };
};
